import { LockOutlined } from "@mui/icons-material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import UpgradePlanCard from "layout/MainLayout/Header/ProfileSection/UpgradePlanCard";
import { memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import Loader from "ui-component/Loader";
import RemainingCredits from "ui-component/RemaningCredits";
import { fromIsoDateToLocaleDate } from "utils/dates";
import { renderPrice } from "views/albums/album-utils";

const CardStyle = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? theme.palette.dark.main
      : theme.palette.primary.light,
  marginBottom: "22px",
  overflow: "hidden",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    width: "157px",
    height: "157px",
    background:
      theme.palette.mode === "dark"
        ? theme.palette.dark.dark
        : theme.palette.primary[200],
    borderRadius: "50%",
    top: "-105px",
    right: "-96px",
  },
}));

const MenuCard = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { data, isLoading } = useSelector(
    (state) => state.dashboard.photograph
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!data) {
    return null;
  }

  return (
    <CardStyle has_full_access={data.has_full_access}>
      <CardContent sx={{ p: 2 }}>
        <List sx={{ p: 0, m: 0 }}>
          <ListItem alignItems="flex-start" disableGutters sx={{ p: 0 }}>
            <ListItemAvatar sx={{ mt: 0 }}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.largeAvatar,
                  color: theme.palette.primary.main,
                  border: theme.palette.mode === "dark" ? "1px solid" : "none",
                  borderColor: theme.palette.primary.main,
                  background: "#fff",
                  marginRight: "12px",
                }}
              >
                <TableChartOutlinedIcon fontSize="inherit" />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              sx={{ mt: 0 }}
              primary={
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.dark.light
                          : theme.palette.primary[800],
                    }}
                  >
                    <FormattedMessage id="profile.subscription" />
                  </Typography>
                </>
              }
              secondary={
                <Chip
                  label={intl.formatMessage(
                    { id: "profile.credits" },
                    { count: data.total_limit }
                  )}
                />
              }
            />
          </ListItem>
        </List>
        <RemainingCredits />
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
          <Grid item xs={12}>
            {!data.has_full_access && (
              <Chip
                variant="filled"
                size="small"
                color="error"
                icon={<LockOutlined />}
                title={
                  <FormattedMessage id="subscription.has_not_full_access_help" />
                }
                label={
                  <FormattedMessage id="subscription.has_not_full_access" />
                }
              />
            )}
            {data.has_full_access && data.subscription_stripe_id && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.grey[500] }}
              >
                <FormattedMessage
                  id="subscription.duration"
                  values={{
                    date: fromIsoDateToLocaleDate(
                      data.subscription_stripe_expires_at
                    ),
                  }}
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.dark.light
                        : theme.palette.primary[800],
                  }}
                >
                  <FormattedMessage
                    id="profile.fee_in_percent"
                    values={{
                      min_order_percent_fee: renderPrice(
                        data.min_order_percent_fee
                      ),
                    }}
                  />
                </Typography>
              </Grid>
              <Grid item>
                <FormattedMessage
                  id="general.prcent_value"
                  values={{
                    value: data.fee_in_percent,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction="column" spacing={1} sx={{ mt: 0 }}>
          <Grid item>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.dark.light
                        : theme.palette.primary[800],
                  }}
                >
                  <FormattedMessage
                    id="profile.fix_fee"
                    values={{
                      min_order_percent_fee: renderPrice(
                        data.min_order_percent_fee
                      ),
                    }}
                  />
                </Typography>
              </Grid>
              <Grid item>
                <FormattedMessage
                  id="general.price_eur"
                  values={{
                    price: Math.round(data.fix_fee / 100, 2),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {!data.has_full_access && <UpgradePlanCard />}
      </CardContent>
    </CardStyle>
  );
};

export default memo(MenuCard);
